import { observer, useLocalObservable } from 'mobx-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  encodeProjectsParams,
  SELECTED_PROJECT_TABS,
  selectedProjectTabPageSize
} from '../constants';
import ProfilerSearchResultsTabEntry from './ProfilerSearchResultsTabEntry';
import Pagination from '../../../components/navigation/Pagination';
import useHistory from '../../../hooks/useHistory';
import { paths } from '../../../utils/constants/routes';
import { useStore } from '../../../store/store';
import MobileSheet from '../../../components/common/MobileSheet';
import ResultsMobileActionBar from '../../../components/common/ResultsMobileActionBar';
import { isInsideResultsMobileBar } from '../../../utils/resultsMobileBar';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../components/buttons/IconButton';
import { runInAction } from 'mobx';
import {
  getEffectiveChartSort,
  isSortOptionVisibleInChart
} from '../../../components/profiler/ProfilerBarChart/chartSortDefaults';
import profilerSearchSortOptions from '../../search/SearchResults/constants/profilerSearchSortOptions';
import {
  getActiveSpotlightsByProjectView,
  getColumnFiltersByProjectView,
  getInitialProfilerColumnsProjectView,
  getProfilerColumnsByProjectView,
  getProfilerExtendedFinancialsToggleByProjectView,
  getProfilerHeatmapColorCodingSelectionByProjectView,
  getProfilerHeatmapGroupingToggleByProjectView,
  getProfilerHeatmapTileSizeSelectionByProjectView,
  getProfilerPreFinancialsOnlySelectionsByProjectView,
  getProfilerSortSelectionByProjectView,
  getProfilerViewSelectionByProjectView,
  getProfilerHeatmapCollapsedByProjectView,
  hasSavedProfilerHeatmapGroupingToggleByProjectView,
  buildProfilerProjectGridTemplate,
  isProfilerListVisible,
  isProfilerProjectColumnVisible,
  DEFAULT_PROFILER_VIEW,
  PROFILER_PROJECT_VIEW_COLUMNS,
  PROFILER_VIEWS,
  PROFILER_VIEWS_LIST,
  saveActiveSpotlightsByProjectView,
  saveColumnFiltersByProjectView,
  saveProfilerColumnsByProjectView,
  saveProfilerExtendedFinancialsToggleByProjectView,
  saveProfilerHeatmapColorCodingSelectionByProjectView,
  saveProfilerHeatmapCollapsedByProjectView,
  saveProfilerHeatmapGroupingToggleByProjectView,
  saveProfilerHeatmapTileSizeSelectionByProjectView,
  saveProfilerPreFinancialsOnlySelectionsByProjectView,
  saveProfilerSortSelectionByProjectView,
  saveProfilerViewSelectionByProjectView,
  sortProfilerResults
} from '../profilerProjectViewConstants';
// V2.0 — Spotlights + column-value filtering (reuse V1 components / engine)
import SpotlightsBar from '../../search/SearchResults/components/SpotlightsBar';
import ColumnFilterTrigger from '../../search/SearchResults/components/ColumnFilterTrigger';
// TD-2465 — bring the TD-2456 clickable-pill filter rail to the project view,
// reusing the shared rail component + store actions (no duplicated logic).
import PillFilterRail from '../../search/SearchResults/components/PillFilterRail';
import AddFilterAnchor from '../../search/SearchResults/components/AddFilterAnchor';
import AddFilterPanel from '../../search/SearchResults/components/AddFilterPanel';
import UnifiedChipRail from '../../search/SearchResults/components/UnifiedChipRail';
import { isUnifiedFiltersEnabled } from '../../../utils/constants/variables';
import {
  applyExploreFilters,
  buildSpotlightContext,
  getSpotlightCounts,
  computeComplement,
  hasEffectiveActiveFilter,
  canRemoveFilteredSet,
  canKeepMatchesSet
} from '../../search/SearchResults/constants/exploreFilterEngine';
import {
  SPOTLIGHT_DEFINITIONS,
  buildExcludedSpotlightIds
} from '../../search/SearchResults/constants/spotlightDefinitions';
import { createPillFilterActions } from '../../search/SearchResults/constants/pillFilters';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import SortableColumnItem from '../../../components/common/SortableColumnItem';
import {
  FINANCIALS_ONLY_COLUMNS,
  FINANCIALS_METRIC_COLUMNS,
  reorderProfilerColumns
} from '../../../utils/constants/profilerColumns';
import {
  restrictToFirstScrollableAncestor,
  restrictToParentElement,
  restrictToVerticalAxis
} from '@dnd-kit/modifiers';
import { UI_OPTIONS, UI_OPTIONS_UTILS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import { DATA_SOURCE, dataSourceClass } from '../../../utils/constants/dataSources';
import ProfilerColumnHeaderRow from '../../../components/profiler/ProfilerColumnHeaderRow/ProfilerColumnHeaderRow';
import ProfilerHeatmapSection from '../../../components/profiler/HeatmapSection/ProfilerHeatmapSection';
import ProfilerBarChart from '../../../components/profiler/ProfilerBarChart/ProfilerBarChart';
import {
  coerceColumnFiltersForLite,
  coerceHeatmapColorCodingForLite,
  coerceHeatmapTileSizeForLite,
  coerceProfilerColumnsForLite,
  coerceDisplayedProfilerSort,
  filterHeatmapColorCodingOptionsForAccess,
  filterHeatmapTileSizeOptionsForAccess,
  gateProfilerSortOptions,
  hasEbitMarginData
} from '../../search/SearchResults/constants/constants';
import { hasKnownRegisterStatus } from '../../../utils/companyStatus';

const ProfilerSearchResultsTabView = observer(
  ({
    project = {},
    results = [],
    forTab = SELECTED_PROJECT_TABS.RESULTS,
    params = {},
    excludeResult = () => {},
    entriesInAction = {},
    displayDetails = () => {},
    isFromAgentView = false,
    fullResultsCount,
    // V2.0 — parent (`ProfilerProjectView`) registers a setter so it can
    // surface the filtered count in the tab label ("Search results (4/73)")
    // when V1 Spotlights / column filters narrow the visible set. Optional
    // for safety in case the component is mounted elsewhere.
    onFilteredCountChange = () => {},
    // TD-2575 — under the unified-filter flag the parent relocates its
    // Request-feedback / info / download buttons into this toolbar; they arrive
    // as pre-rendered nodes (the parent keeps the download subsystem + reactivity).
    feedbackSlot = null,
    trailingActionsSlot = null,
    // TD-2769 — parent-owned project-level controls (data source / download /
    // feedback) rendered inside this component's bottom mobile action bar.
    mobilePrimaryAction = null,
    mobileMoreActions = [],
    // TD-2461 × TD-2560 — parent-owned bulk-exclude dispatch (one companyIds[]
    // request); this store computes the target id sets + owns the menu UI.
    onBulkExclude = () => Promise.resolve(),
    // Harmonized toolbar mount — the parent renders an anchor ABOVE the project
    // tab strip and hands us its DOM node so we can portal the toolbar bar there
    // (keeping it driven by this store). Null on mobile / flag-off → toolbar
    // falls back to its in-grid position.
    toolbarMountNode = null
  }) => {
    const { utilsStore, profilerAccessStore } = useStore();
    const { navigate } = useHistory();

    const state = useLocalObservable(() => ({
      forTab: forTab,
      setForTab: (value = '') => (state.forTab = value),
      params: params,
      isFromAgentView: isFromAgentView,
      scrollToProjectViewScrollStartingPoint: (force = false) => {
        setTimeout(() => {
          const el = document.getElementById('project-view-scroll-starting-point');
          const header = document.querySelector('header');
          if (el) {
            const headerHeight = header?.clientHeight || 0;
            const newTop = el.offsetTop - headerHeight;
            if (window.scrollY < newTop || force) {
              window.scrollTo({
                top: newTop,
                behavior: 'smooth'
              });
            }
          }
        }, 100);
      },
      selectedSort: getProfilerSortSelectionByProjectView(project.searchId),
      selectSortOption: (value = '') => {
        state.selectedSort = value;
        // Persisted like every other project-view setting (same key as the team view).
        saveProfilerSortSelectionByProjectView(value, project.searchId);
        navigate(
          (state.isFromAgentView ? paths.PROJECTS : paths.PROFILER_PROJECTS) +
            '?params=' +
            encodeProjectsParams({ ...state.params, selectedProjectTabPage: 1 })
        );
        state.scrollToProjectViewScrollStartingPoint(true);
      },
      get currentResults() {
        const filtered =
          state.forTab === SELECTED_PROJECT_TABS.RESULTS
            ? results.filter((r) => !r.isExcluded)
            : results.filter((r) => r.isExcluded);
        return sortProfilerResults(filtered, state.selectedSort);
      },
      isSortToggled: false,
      toggleSort: () => {
        state.isSortToggled = !state.isSortToggled;
        if (state.isSortToggled) {
          state.scrollToProjectViewScrollStartingPoint();
        }
      },
      get profilerSortOptions() {
        const options = project.searchCategory
          ? profilerSearchSortOptions.filter((o) =>
              o.onlyForSearchCategory ? o.onlyForSearchCategory === project.searchCategory : true
            )
          : profilerSearchSortOptions;

        // TD-2890 — keyed on the data, not the tier: the BE omits the EBIT block from
        // accounts without EBIT entitlement, so an empty set is the answer. Gated here
        // rather than in the menu's column filter, which is bypassed below 1536px (and in
        // Chart view) and so left the options offered.
        return gateProfilerSortOptions(options, {
          hasEbitData: state.hasEbitMarginData,
          hasRegisterStatusData: state.hasRegisterStatusData
        });
      },
      // TD-2719 — the sort the results are really ordered by: the Chart view falls back to
      // EBIT-margin ↓ while nothing is chosen. Display only — applying and persisting a
      // sort stays on `selectedSort`, so List and Heatmap keep relevance order.
      //
      // Reads the named constant, not a positional index. This line merged cleanly against
      // TD-2733 without conflicting even though that ticket deleted
      // `PROFILER_PROJECT_VIEW_VIEWS_LIST` outright — a clean merge that would not have built.
      get hasEbitMarginData() {
        return hasEbitMarginData(results);
      },
      get hasRegisterStatusData() {
        return hasKnownRegisterStatus(results);
      },
      get effectiveSort() {
        return coerceDisplayedProfilerSort(
          getEffectiveChartSort(state.selectedSort, state.profilerView === PROFILER_VIEWS.CHART),
          {
            hasEbitData: state.hasEbitMarginData,
            hasRegisterStatusData: state.hasRegisterStatusData
          }
        );
      },
      get layout() {
        return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
      },
      profilerColumns: coerceProfilerColumnsForLite(
        getProfilerColumnsByProjectView(project.searchId) ||
          getInitialProfilerColumnsProjectView(
            project.fields,
            utilsStore.windowWidth,
            profilerAccessStore.isProfilerLiteOnly
          ),
        profilerAccessStore.isProfilerLiteOnly
      ),

      isProfilerExtendedFinancialsToggled: getProfilerExtendedFinancialsToggleByProjectView(
        project.searchId
      ),
      toggleProfilerExtendedFinancials: () => {
        state.isProfilerExtendedFinancialsToggled = !state.isProfilerExtendedFinancialsToggled;
        saveProfilerExtendedFinancialsToggleByProjectView(
          state.isProfilerExtendedFinancialsToggled,
          project.searchId
        );
      },
      isProfilerCustomizeColumnsToggled: false,
      toggleProfilerCustomizeColumns: () => {
        state.isProfilerCustomizeColumnsToggled = !state.isProfilerCustomizeColumnsToggled;
        if (state.isProfilerCustomizeColumnsToggled) {
          state.scrollToProjectViewScrollStartingPoint();
        }
      },
      isProfilerViewMenuToggled: false,
      toggleProfilerViewMenu: () => {
        state.isProfilerViewMenuToggled = !state.isProfilerViewMenuToggled;
        if (state.isProfilerViewMenuToggled) {
          state.scrollToProjectViewScrollStartingPoint();
        }
      },
      profilerView: getProfilerViewSelectionByProjectView(project.searchId),
      setProfilerView: (viewValue) => {
        state.profilerView = viewValue;
        saveProfilerViewSelectionByProjectView(viewValue, project.searchId);
        state.scrollToProjectViewScrollStartingPoint(true);
      },
      profilerHeatmapTileSize: coerceHeatmapTileSizeForLite(
        getProfilerHeatmapTileSizeSelectionByProjectView(project.searchId),
        profilerAccessStore.isProfilerLiteOnly
      ),
      setProfilerHeatmapTileSize: (newValue) => {
        state.profilerHeatmapTileSize = newValue;
        saveProfilerHeatmapTileSizeSelectionByProjectView(newValue, project.searchId);
      },
      isProfilerHeatmapTileSizeMenuToggled: false,
      toggleProfilerHeatmapTileSizeMenu: () => {
        state.isProfilerHeatmapTileSizeMenuToggled = !state.isProfilerHeatmapTileSizeMenuToggled;
      },
      get profilerHeatmapTileSizeOptions() {
        return filterHeatmapTileSizeOptionsForAccess(
          UI_OPTIONS_UTILS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE],
          !profilerAccessStore.isProfilerLiteOnly
        );
      },
      profilerHeatmapColorCoding: coerceHeatmapColorCodingForLite(
        getProfilerHeatmapColorCodingSelectionByProjectView(project.searchId),
        profilerAccessStore.isProfilerLiteOnly
      ),
      setProfilerHeatmapColorCoding: (newValue) => {
        state.profilerHeatmapColorCoding = newValue;
        saveProfilerHeatmapColorCodingSelectionByProjectView(newValue, project.searchId);
      },
      isProfilerHeatmapColorCodingMenuToggled: false,
      toggleProfilerHeatmapColorCodingMenu: () => {
        state.isProfilerHeatmapColorCodingMenuToggled =
          !state.isProfilerHeatmapColorCodingMenuToggled;
      },
      get profilerHeatmapColorCodingOptions() {
        return filterHeatmapColorCodingOptionsForAccess(
          UI_OPTIONS_UTILS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING],
          !profilerAccessStore.isProfilerLiteOnly
        );
      },
      // TD-2733 — folding the heatmap hands the list the whole viewport without leaving
      // the view, and without losing the metric selections, which stay on this store.
      // Deliberately does NOT scroll: collapsing already changes the section header's
      // height, the measured var, the heatmap height and therefore the document height,
      // and a smooth scroll on top of all that lands somewhere unpredictable.
      isProfilerHeatmapCollapsed: getProfilerHeatmapCollapsedByProjectView(project.searchId),
      toggleProfilerHeatmapCollapsed: () => {
        state.isProfilerHeatmapCollapsed = !state.isProfilerHeatmapCollapsed;
        saveProfilerHeatmapCollapsedByProjectView(
          state.isProfilerHeatmapCollapsed,
          project.searchId
        );
      },
      isProfilerHeatmapGroupingToggled: getProfilerHeatmapGroupingToggleByProjectView(
        project.searchId
      ),
      toggleProfilerHeatmapGrouping: () => {
        state.isProfilerHeatmapGroupingToggled = !state.isProfilerHeatmapGroupingToggled;
        saveProfilerHeatmapGroupingToggleByProjectView(
          state.isProfilerHeatmapGroupingToggled,
          project.searchId
        );
        state.toggleProfilerHeatmapGroupingCallback(state.isProfilerHeatmapGroupingToggled);
      },
      toggleProfilerHeatmapGroupingCallback: () => {},
      setToggleProfilerHeatmapGroupingCallback: (func = () => {}) => {
        runInAction(() => {
          state.toggleProfilerHeatmapGroupingCallback = func;
        });
      },
      get profilerHeatmapGroup() {
        if (!state.isProfilerHeatmapGroupingToggled || state.disableProfilerHeatmapGroup) {
          return null;
        }

        let group = null;
        if (
          project.searchCategory ===
          UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries']
        ) {
          group = '_industryCode';
        } else if (
          project.searchCategory ===
          UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY].Characteristics
        ) {
          group = 'scoreLabel';
        }

        return group;
      },
      get disableProfilerHeatmapGroup() {
        let group = null;
        if (
          project.searchCategory ===
          UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries']
        ) {
          group = '_industryCode';
        } else if (
          project.searchCategory ===
          UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY].Characteristics
        ) {
          group = 'scoreLabel';
        }

        if (!group) {
          return true;
        }

        return new Set(state.currentResults.map((company) => company[group])).size < 2;
      },
      // ====================================================================
      // V2.0 — Spotlights + column-value filtering for the Project view.
      //
      // Mirrors the V1 state shape on `SearchResults/index.jsx` exactly so
      // the existing `SpotlightsBar`, `ColumnFilterTrigger`, and
      // `ColumnFilterPopover` components plug in without modification.
      //
      // Hydration: each `*ByProjectView` get-helper reads the project-
      // specific localStorage key first, then falls back to the
      // SearchResults-page key — so a freshly opened project inherits the
      // analyst's last filter set from the originating search. Saves only
      // write to the project key (`asbpv-` / `cfbpv-`), so per-project
      // overrides never leak back to the SearchResults page.
      // ====================================================================
      activeSpotlights: new Set(getActiveSpotlightsByProjectView(project.searchId)),
      columnFilters: coerceColumnFiltersForLite(
        getColumnFiltersByProjectView(project.searchId),
        profilerAccessStore.isProfilerLiteOnly
      ),
      openFilterColumn: null,
      // V1.8 in-memory tracking for symmetric column un-hide on Spotlight
      // deactivate. Not persisted — on reload we treat any visible column
      // as analyst-owned (safer default).
      columnsAutoShownBySpotlight: new Set(),
      toggleSpotlight: (spotlightId) => {
        const wasActive = state.activeSpotlights.has(spotlightId);
        if (wasActive) {
          state.activeSpotlights.delete(spotlightId);
        } else {
          state.activeSpotlights.add(spotlightId);
        }
        saveActiveSpotlightsByProjectView([...state.activeSpotlights], project.searchId);

        // V1.7 — Spotlight ↔ column filter sync. Activating a Spotlight
        // populates the related column filter; deactivating clears it.
        // Auto-show + V1.8 symmetric un-hide of the related column are
        // mirrored from SearchResults/index.jsx verbatim.
        const def = SPOTLIGHT_DEFINITIONS.find((d) => d.id === spotlightId);
        const rel = def?.relatedColumn;
        if (!rel) return;

        if (wasActive) {
          state.clearColumnFilter(rel.columnKey);
          if (state.columnsAutoShownBySpotlight.has(spotlightId)) {
            const col = state.profilerColumns.find((c) => c.id === rel.columnId);
            if (col) {
              col.selected = false;
              saveProfilerColumnsByProjectView(state.profilerColumns, project.searchId);
            }
            state.columnsAutoShownBySpotlight.delete(spotlightId);
          }
        } else {
          // Compute the filter value against the non-excluded results — same
          // semantics as the SearchResults page (Top-quartile EBIT's
          // threshold is computed against the loaded set).
          const base = results.filter((c) => !c.isExcluded);
          const filterValue = rel.computeFilterValue(base);
          state.setColumnFilter(rel.columnKey, filterValue);

          const col = state.profilerColumns.find((c) => c.id === rel.columnId);
          if (col?.selected === false) {
            col.selected = true;
            saveProfilerColumnsByProjectView(state.profilerColumns, project.searchId);
            state.columnsAutoShownBySpotlight.add(spotlightId);
          }
        }
      },
      setColumnFilter: (columnKey, filterValue) => {
        if (filterValue == null) {
          delete state.columnFilters[columnKey];
        } else {
          state.columnFilters[columnKey] = filterValue;
        }
        state.columnFilters = { ...state.columnFilters };
        saveColumnFiltersByProjectView(state.columnFilters, project.searchId);
      },
      // TD-2465 (mirrors TD-2456 on SearchResults) — toggle a clicked pill value
      // on/off in the given column's keyword-cloud filter (Industry / Value chain
      // / Products). Multiple pills in the same column combine with AND; across
      // columns the engine already ANDs. Removing the last value clears the
      // column filter. Delegates to setColumnFilter, so per-project persistence
      // (`cfbpv-` key, Set⇄Array via _columnFiltersToJson) is inherited.
      // TD-2465 — clickable-pill store actions (togglePillFilter /
      // setPillFilterMode), shared verbatim with the SearchResults page via
      // createPillFilterActions. The toggle matches the clicked pill
      // case-insensitively (normalizeKeywordQuery), so a lit case-variant pill
      // clears instead of appending a near-duplicate. Delegates to
      // setColumnFilter, so per-project persistence (`cfbpv-` key, Set⇄Array via
      // _columnFiltersToJson) is inherited. The chip-rail subset + clear-all
      // live in the shared PillFilterRail component.
      ...createPillFilterActions(() => state, {
        // Any/OR is the unified model's default (TD-2561); the current pill UI
        // ships All/AND, so the flip arrives with the flag that introduces it.
        defaultMode: isUnifiedFiltersEnabled ? 'or' : 'and'
      }),
      clearColumnFilter: (columnKey) => {
        if (!(columnKey in state.columnFilters)) return;
        delete state.columnFilters[columnKey];
        state.columnFilters = { ...state.columnFilters };
        saveColumnFiltersByProjectView(state.columnFilters, project.searchId);
      },
      clearAllFilters: () => {
        state.activeSpotlights.clear();
        state.columnFilters = {};
        state.openFilterColumn = null;
        state.columnsAutoShownBySpotlight.clear();
        saveActiveSpotlightsByProjectView([], project.searchId);
        saveColumnFiltersByProjectView({}, project.searchId);
      },
      setOpenFilterColumn: (columnKey) => {
        state.openFilterColumn = columnKey;
      },
      // Unified-filter model — Add-filter panel UI state (not persisted) + the
      // `filteredResults` / `sortedResults` aliases so the shared Add-filter
      // panel and chip rail (written against the SearchResults store) read the
      // same interface on this project store.
      isAddFilterPanelOpen: false,
      addFilterDimension: null,
      openAddFilterPanel: (dimensionKey = null) => {
        if (dimensionKey) state.addFilterDimension = dimensionKey;
        state.isAddFilterPanelOpen = true;
        state.openFilterColumn = null;
      },
      closeAddFilterPanel: () => {
        state.isAddFilterPanelOpen = false;
      },
      setAddFilterDimension: (dimensionKey) => {
        state.addFilterDimension = dimensionKey;
      },
      // Bridge the global Profiler-Lite role onto the project store so the shared
      // Add-filter panel can hide the EBIT-margin dimension for Lite (parity with
      // SearchResults — TD-2499 review).
      get isProfilerLiteOnly() {
        return profilerAccessStore.isProfilerLiteOnly;
      },
      get filteredResults() {
        return state.filteredCurrentResults;
      },
      get sortedResults() {
        return state.currentResults;
      },
      get categoryExcludedSpotlightIds() {
        // Spotlights that must not be offered here. Excluded from filtering
        // too, so a stale / inherited active ID can't silently empty the list
        // with no chip.
        //
        // TD-2898 — the same helper the search store uses. This getter used to
        // hide "High matches" on Industry projects and nothing else, so a Lite
        // account kept a live "Top-quartile EBIT" chip on a saved project even
        // though the panel around it already withheld the EBIT filter
        // dimension (which gates on `isProfilerLiteOnly` instead).
        return buildExcludedSpotlightIds({
          searchCategory: project.searchCategory,
          isProfilerLiteOnly: profilerAccessStore.isProfilerLiteOnly,
          hasEbitData: state.hasEbitMarginData
        });
      },
      get filteredCurrentResults() {
        // Apply Spotlights ∩ column filters on top of the already-sorted +
        // tab-filtered `currentResults`. Heatmap and Chart continue to
        // read `currentResults` directly (V1 spec: only the List view row
        // slice + List-view pagination respect the new filters).
        const ctx = buildSpotlightContext(state.currentResults);
        // Drop Spotlights hidden for this category — a stale / inherited
        // active ID must not silently filter the list to empty with no chip.
        const excluded = state.categoryExcludedSpotlightIds;
        const activeForFilter = excluded
          ? new Set([...state.activeSpotlights].filter((id) => !excluded.has(id)))
          : state.activeSpotlights;
        return applyExploreFilters(state.currentResults, activeForFilter, state.columnFilters, ctx);
      },
      get spotlightCounts() {
        // Honest live counts: what would this Spotlight add to the current
        // column-filtered set. Operates on the unfiltered (by V1) tab set
        // so the quartile context stays stable across user interactions.
        return getSpotlightCounts(state.currentResults, state.columnFilters);
      },
      // TD-2461 × TD-2560 — bulk exclusion, wired to the SAME unified filter
      // state + engine the chip rail uses. Matching set = filteredCurrentResults;
      // complement = the non-excluded rows that don't match. The parent owns the
      // dispatch (onBulkExclude → one companyIds[] request); this store owns the
      // menu + in-flight UI. Mirrors the SearchResults store (index.jsx).
      isBulkExcludeMenuToggled: false,
      toggleBulkExcludeMenu: (force) => {
        state.isBulkExcludeMenuToggled =
          typeof force === 'boolean' ? force : !state.isBulkExcludeMenuToggled;
      },
      massExcludeInFlight: false,
      get keptResults() {
        return state.filteredCurrentResults;
      },
      get complementResults() {
        const base = results.filter((r) => !r.isExcluded);
        return computeComplement(base, state.filteredCurrentResults);
      },
      get keptCount() {
        return state.keptResults.length;
      },
      get complementCount() {
        return state.complementResults.length;
      },
      get hasEffectiveFilter() {
        return hasEffectiveActiveFilter(
          state.activeSpotlights,
          state.columnFilters,
          state.categoryExcludedSpotlightIds
        );
      },
      get isBulkExcluding() {
        return state.massExcludeInFlight;
      },
      get canRemoveFiltered() {
        return canRemoveFilteredSet({
          hasEffectiveFilter: state.hasEffectiveFilter,
          keptCount: state.keptCount,
          isBulkExcluding: state.isBulkExcluding
        });
      },
      get canKeepMatches() {
        return canKeepMatchesSet({
          hasEffectiveFilter: state.hasEffectiveFilter,
          keptCount: state.keptCount,
          complementCount: state.complementCount,
          isBulkExcluding: state.isBulkExcluding
        });
      },
      excludeMatching: () => {
        if (!state.canRemoveFiltered) return;
        // Snapshot before dispatch — flipping isExcluded recomputes keptResults.
        state._runBulkExclude(state.keptResults.slice());
      },
      excludeComplement: () => {
        if (!state.canKeepMatches) return;
        state._runBulkExclude(state.complementResults.slice());
      },
      _runBulkExclude: (entries) => {
        if (!entries.length) return;
        state.isBulkExcludeMenuToggled = false;
        state.massExcludeInFlight = true;
        const companyIds = entries.map((entry) => entry.id);
        // Invoke onBulkExclude INSIDE the chain (not as an argument to
        // Promise.resolve) so a synchronous throw becomes a rejection the
        // `.finally` still observes — the in-flight latch can never stick on
        // (which would leave the control dead until remount).
        void Promise.resolve()
          .then(() => onBulkExclude(companyIds))
          .finally(state._onBulkExcludeSettled);
      },
      _onBulkExcludeSettled: () => {
        runInAction(() => {
          state.massExcludeInFlight = false;
        });
      },
      // `state.results` is owned by the parent ProfilerProjectView and
      // mirrors what `state.results` does on SearchResults — but it lives
      // in this component as the prop `results`. Expose it on `state` so
      // the ColumnFilterPopover (which reads `state.results` for distrib.)
      // gets the same data shape as the SearchResults variant.
      get results() {
        return results;
      },
      get isInitialLoading() {
        // The Project view doesn't have the same initial-loading gate as
        // SearchResults (its `results` are passed in as a prop, already
        // loaded). Surface a constant `false` so the ColumnFilterTrigger
        // and SpotlightsBar load-gate checks evaluate correctly.
        return false;
      },
      get isLoading() {
        return false;
      }
    }));

    useEffect(() => {
      state.setForTab(forTab);
    }, [state, forTab]);

    useEffect(() => {
      runInAction(() => {
        state.isFromAgentView = isFromAgentView;
      });
    }, [state, isFromAgentView]);

    useEffect(() => {
      runInAction(() => {
        state.params = params;
      });
    }, [state, params]);

    // V2.0 — push the V1-filtered count up to the parent so the
    // `Search results (X/Y)` tab label can reflect what the analyst
    // actually sees. Only report while on the RESULTS tab — the parent's
    // "Search results" label has no relationship to filters applied on
    // the Excluded tab, so pushing that count up would mislabel the
    // results tab whenever the analyst toggles to Excluded with filters
    // active.
    const filteredCount =
      forTab === SELECTED_PROJECT_TABS.RESULTS ? state.filteredCurrentResults.length : null;
    useEffect(() => {
      onFilteredCountChange(filteredCount);
    }, [filteredCount, onFilteredCountChange]);

    // V2.0 — clear the parent's filtered count when this view unmounts (e.g.
    // navigating to the Comments tab) so the "Search results (X/Y)" tab label
    // doesn't freeze at a stale ratio.
    useEffect(() => {
      return () => onFilteredCountChange(null);
    }, [onFilteredCountChange]);

    useEffect(() => {
      const handleClickOutsideSort = (event) => {
        const isClickInsideSortMenu = event.target.closest('.sort-menu-wrap');
        const isClickInsideSortButton = event.target.closest('#sort-btn');

        if (
          isClickInsideSortMenu ||
          isClickInsideSortButton ||
          // TD-2769: on mobile the View/Sort menus are bottom sheets; a tap on the
          // bar button or inside the sheet must not count as an outside-click.
          isInsideResultsMobileBar(event.target)
        ) {
          // do nothing
        } else {
          if (state.isSortToggled) {
            state.toggleSort();
          }
        }
      };

      document.addEventListener('click', handleClickOutsideSort);

      return () => {
        document.removeEventListener('click', handleClickOutsideSort);
      };
    }, [state]);

    useEffect(() => {
      const handleClickOutsideSort = (event) => {
        const isClickInsideSortMenu = event.target.closest('.view-menu-wrap');
        const isClickInsideSortButton = event.target.closest('#list-change-btn');

        if (
          isClickInsideSortMenu ||
          isClickInsideSortButton ||
          // TD-2769: on mobile the View/Sort menus are bottom sheets; a tap on the
          // bar button or inside the sheet must not count as an outside-click.
          isInsideResultsMobileBar(event.target)
        ) {
          // do nothing
        } else {
          if (state.isProfilerViewMenuToggled) {
            state.toggleProfilerViewMenu();
          }
        }
      };

      document.addEventListener('click', handleClickOutsideSort);

      return () => {
        document.removeEventListener('click', handleClickOutsideSort);
      };
    }, [state]);

    // TD-2499: if a Lite user restored a persisted Chart view, fall back to the default
    // view (same guard SearchResults applies for the EBIT-centric chart).
    useEffect(() => {
      if (
        (utilsStore.windowWidth < 600 || profilerAccessStore.isProfilerLiteOnly) &&
        state.profilerView === PROFILER_VIEWS.CHART
      ) {
        state.setProfilerView(DEFAULT_PROFILER_VIEW);
      }
    }, [state, utilsStore.windowWidth, profilerAccessStore.isProfilerLiteOnly]);

    useEffect(() => {
      const handleClickOutsideBulkExclude = (event) => {
        const isInsideMenu = event.target.closest('.bulk-exclude-menu-wrap');
        const isTrigger = event.target.closest('#bulk-exclude-trigger');
        if (!isInsideMenu && !isTrigger && state.isBulkExcludeMenuToggled) {
          state.toggleBulkExcludeMenu(false);
        }
      };

      document.addEventListener('click', handleClickOutsideBulkExclude);

      return () => {
        document.removeEventListener('click', handleClickOutsideBulkExclude);
      };
    }, [state]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        const isInsideColumnsPopup = event.target.closest('.customize-columns-main-wrap');
        const isCustomizeColumnsButton = event.target.closest('#customize-columns-button');

        if (
          !isInsideColumnsPopup &&
          !isCustomizeColumnsButton &&
          // TD-2769: Customize is a bottom sheet on mobile — exempt the bar/sheets.
          !isInsideResultsMobileBar(event.target)
        ) {
          if (state.isProfilerCustomizeColumnsToggled) {
            state.toggleProfilerCustomizeColumns();
          }
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [state]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        const isClickInsideMenu = event.target.closest('.heatmap-control-menu-wrap.tile-size');
        const isClickButton = event.target.closest('#profiler-tile-size-metric');

        if (isClickInsideMenu || isClickButton) {
          // do nothing
        } else {
          if (state.isProfilerHeatmapTileSizeMenuToggled) {
            state.toggleProfilerHeatmapTileSizeMenu();
          }
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }, [state]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        const isClickInsideMenu = event.target.closest('.heatmap-control-menu-wrap.color-coding');
        const isClickButton = event.target.closest('#profiler-color-coding-metric');

        if (isClickInsideMenu || isClickButton) {
          // do nothing
        } else {
          if (state.isProfilerHeatmapColorCodingMenuToggled) {
            state.toggleProfilerHeatmapColorCodingMenu();
          }
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }, [state]);

    useEffect(() => {
      if (hasSavedProfilerHeatmapGroupingToggleByProjectView(project.searchId)) {
        return;
      }

      if (!state.isProfilerHeatmapGroupingToggled && !state.disableProfilerHeatmapGroup) {
        state.toggleProfilerHeatmapGrouping();
      }
    }, []);

    const getIsExcludingResult = (companyId) => {
      return !!entriesInAction[companyId];
    };

    // V2.0 — pagination counts off the List-view filtered set so the page
    // count matches the rows the analyst actually sees. Heatmap and Chart
    // render off `currentResults` further down and ignore pagination.
    const showPagination = state.filteredCurrentResults.length > selectedProjectTabPageSize;
    const pages = Math.ceil(state.filteredCurrentResults.length / selectedProjectTabPageSize) || 1;
    // V2.0 — clamp the page index to the filtered page count. When Spotlights /
    // column filters narrow the set, the URL's `selectedProjectTabPage` can
    // point past the last page; without this clamp the row slice below renders
    // an empty list while `showPagination` hides the pager, stranding the
    // analyst. Clamps to the last valid page (same as the SearchResults page).
    const page = Math.min(Math.max((params.selectedProjectTabPage || 1) - 1, 0), pages - 1);

    const profilerColumns = state.profilerColumns;

    const profilerColumnHeaderJSX = {
      [PROFILER_PROJECT_VIEW_COLUMNS.COMPANY_NAME]: (
        <div
          className="col name"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.COMPANY_NAME
            ).minWidth
          }}
        >
          <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Company name</span>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.LOCATION]: (
        <div
          className="col headquarter"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.LOCATION
            ).minWidth
          }}
        >
          <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Location</span>
          <ColumnFilterTrigger columnKey="headquarters" state={state} />
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.COMPANY_DESCRIPTION]: (
        <div
          className={`col company-description ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.COMPANY_DESCRIPTION
            )?.minWidth
          }}
        >
          Company description
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.PRODUCT_PORTFOLIO_DESCRIPTION]: (
        <div
          className={`col product-portfolio-description ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.PRODUCT_PORTFOLIO_DESCRIPTION
            )?.minWidth
          }}
        >
          Portfolio description
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.KEY_PRODUCTS]: (
        <div
          className={`col key-products ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.KEY_PRODUCTS
            )?.minWidth
          }}
        >
          Key products
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.KEY_END_MARKETS]: (
        <div
          className={`col key-end-markets ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.KEY_END_MARKETS
            )?.minWidth
          }}
        >
          Key end-markets
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.INDUSTRY]: (
        <div
          className={`col industry ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.INDUSTRY
            ).minWidth
          }}
        >
          Industry
          <ColumnFilterTrigger columnKey="industries" state={state} />
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.VALUE_CHAIN]: (
        <div
          className={`col value-chain ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.VALUE_CHAIN
            ).minWidth
          }}
        >
          Value chain
          <ColumnFilterTrigger columnKey="valueChain" state={state} />
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.PRODUCTS]: (
        <div
          className={`col products ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.PRODUCTS
            ).minWidth
          }}
        >
          Products
          <ColumnFilterTrigger columnKey="products" state={state} />
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.SALES]: (
        <div
          className="col revenue"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.SALES
            ).minWidth
          }}
        >
          <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Sales (€M)</span>
          <ColumnFilterTrigger columnKey="revenue" state={state} />
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.EMPLOYEES]: (
        <div
          className="col employees"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.EMPLOYEES
            ).minWidth
          }}
        >
          <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Employees</span>
          <ColumnFilterTrigger columnKey="employees" state={state} />
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.CAGR3Y]: (
        <div
          className="col cagr"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.CAGR3Y
            )?.minWidth
          }}
        >
          <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>CAGR 3Y (%)</span>
          <ColumnFilterTrigger columnKey="cagr3Y" state={state} />
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.CAGR4Y]: (
        <div
          className="col cagr"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.CAGR4Y
            )?.minWidth
          }}
        >
          <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>CAGR 4Y (%)</span>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.EBIT_MARGIN]: (
        <div
          className="col ebit-margin"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.EBIT_MARGIN
            )?.minWidth
          }}
        >
          <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>EBIT Margin (%)</span>
          <ColumnFilterTrigger columnKey="ebitPercentage" state={state} />
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.SITUATION]: (
        <div
          className="col situation"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.SITUATION
            ).minWidth
          }}
        >
          <span>Situation</span>
          <ColumnFilterTrigger columnKey="situation" state={state} />
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.SHAREHOLDERS]: (
        <div
          className="col shareholders"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.SHAREHOLDERS
            )?.minWidth
          }}
        >
          <span>Shareholders</span>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.FEEDBACK]: (
        <div
          className="col feedback"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.FEEDBACK
            )?.minWidth
          }}
        >
          Feedback results
        </div>
      )
    };

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
      })
    );

    // TD-2465 — visibility gate for the pill-filter rail: List view, results
    // loaded (both loading getters are constant `false` here). The shared
    // <PillFilterRail> handles the "at least one pill active" check + rendering.
    // Unlike SearchResults — whose `.results-header` is display:none on mobile,
    // forcing a separate mobile rail — the project view keeps its header region
    // (`.results-header-top-row.at-project-view` + the `show-also-on-mobile`
    // column-headers) visible on mobile, so a single desktop rail covers both.
    // TD-2733 — the sticky header strip only has content in the placements where the
    // in-grid toolbar renders; mirrors the condition on `.project-sort-wrapper` below.
    const hasHeaderStrip = !isUnifiedFiltersEnabled || state.layout === 'mobile';

    // Bound to this render's viewport and extended-financials state, then handed to both
    // the detached column header and (via the shared helper) the row component, so the
    // grid template and the cells cannot disagree.
    const isProjectColumnVisible = (column) =>
      isProfilerProjectColumnVisible(column, {
        windowWidth: utilsStore.windowWidth,
        isProfilerExtendedFinancialsToggled: state.isProfilerExtendedFinancialsToggled
      });

    const isPillRailVisible =
      isProfilerListVisible(state.profilerView) && !state.isInitialLoading && !state.isLoading;

    // TD-2575 — the results toolbar (Add-filter / Customize / view / sort) is
    // shared between two placements: flag-off it renders inside the grid header
    // actions cell (`.project-sort-wrapper`); flag-on it renders in a free-standing
    // row (`.project-search-filters-wrapper`) matching the Search-results bar, with
    // the parent's Request-feedback (after Customize) and info+download (at the end)
    // spliced in as slots. `harmonized` only adds per-control anchor wrappers (so
    // each menu opens under its own button) + the slots; flag-off output is
    // byte-identical to before.
    const wrapAnchor = (harmonized, node) =>
      harmonized ? <span className="pv-toolbar-anchor">{node}</span> : node;

    // TD-2575 — Customize-columns menu handlers hoisted to component scope. Once
    // the toolbar became a render fn, the inline drag/toggle callbacks nested past
    // the 4-level limit (Sonar S2004) and inflated renderProjectToolbar's cognitive
    // complexity. Hoisting keeps behaviour identical while flattening the nesting.
    // TD-2746: the two column sets and the reorder arithmetic are shared with the
    // search-results popover (utils/constants/profilerColumns). How *this* surface
    // decides the toggle is on stays local and derived — the search page persists a
    // flag instead.
    const isFinancialsOnlyActive = () =>
      !state.profilerColumns.find(
        (column) => !FINANCIALS_ONLY_COLUMNS.includes(column.id) && column.selected
      );
    const handleColumnReorder = (event) => {
      const { active, over } = event;
      if (active.id === over.id) return;
      runInAction(() => {
        state.profilerColumns = reorderProfilerColumns(state.profilerColumns, active.id, over.id);
        saveProfilerColumnsByProjectView(state.profilerColumns, project.searchId);
      });
    };
    const handleToggleColumnSelected = (item) => {
      runInAction(() => {
        item.selected = !item.selected;
        saveProfilerColumnsByProjectView(state.profilerColumns, project.searchId);
      });
    };
    const handleResetColumns = () => {
      runInAction(() => {
        state.profilerColumns = getInitialProfilerColumnsProjectView(
          project.fields,
          utilsStore.windowWidth,
          profilerAccessStore.isProfilerLiteOnly
        );
        saveProfilerColumnsByProjectView(state.profilerColumns, project.searchId);
      });
    };
    const handleToggleFinancialsOnly = () => {
      const turningOn = isFinancialsOnlyActive();
      runInAction(() => {
        if (turningOn) {
          const previousSelections = getProfilerPreFinancialsOnlySelectionsByProjectView(
            project.searchId
          );
          state.profilerColumns.forEach((column) => {
            column.selected = previousSelections
              ? previousSelections.includes(column.id)
              : !FINANCIALS_METRIC_COLUMNS.includes(column.id);
          });
        } else {
          saveProfilerPreFinancialsOnlySelectionsByProjectView(
            state.profilerColumns.filter((column) => column.selected).map((column) => column.id),
            project.searchId
          );
          state.profilerColumns.forEach((column) => {
            column.selected = FINANCIALS_ONLY_COLUMNS.includes(column.id);
          });
        }
        saveProfilerColumnsByProjectView(state.profilerColumns, project.searchId);
      });
    };

    // TD-2769: `wrapClassName` lets one renderer back both the desktop anchored
    // dropdown (`.view-menu-wrap`) and the mobile bottom-sheet body
    // (`.mobile-sheet-options`) with the same option rows.
    const renderProjectViewMenu = (wrapClassName = 'view-menu-wrap') => (
      <div className={wrapClassName}>
        {PROFILER_VIEWS_LIST.slice()
          .filter((view) => {
            // Mobile: hide chart. Lite: hide chart (EBIT-centric) — TD-2499.
            if (utilsStore.windowWidth < 600 || profilerAccessStore.isProfilerLiteOnly) {
              return view.value !== PROFILER_VIEWS.CHART;
            }
            return true;
          })
          .map((view) => (
            <IconButton
              key={view.id}
              hoverType={ICON_BUTTON_TYPES.BLUE}
              withBorder={false}
              innerText={view.name}
              onClick={() => state.setProfilerView(view.value) & state.toggleProfilerViewMenu()}
              disabled={state.profilerView === view.value || !!view.disabled}
              filled={state.profilerView === view.value}
              {...(view.icon ? { iconOnRight: true, icon: view.icon } : {})}
              tooltipText={view.tooltipText || ''}
            />
          ))}
      </div>
    );
    const renderProjectSortMenu = (wrapClassName = 'sort-menu-wrap') => (
      <div className={wrapClassName}>
        {state.profilerSortOptions
          .filter((option) =>
            state.profilerView === PROFILER_VIEWS.CHART
              ? isSortOptionVisibleInChart(option, state.selectedSort)
              : utilsStore.windowWidth >= 1536
                ? !option.onlyForColumn ||
                  !!profilerColumns.find(
                    (column) => column.id === option.onlyForColumn && column.selected
                  )
                : true
          )
          .map((option) => (
            <IconButton
              key={option.value}
              hoverType={ICON_BUTTON_TYPES.BLUE}
              withBorder={false}
              innerText={option.shortName || option.name}
              onClick={() => state.selectSortOption(option.value) & state.toggleSort()}
              disabled={state.effectiveSort === option.value}
              filled={state.effectiveSort === option.value}
              {...(option.icon ? { iconOnRight: true, icon: option.icon } : {})}
            />
          ))}
      </div>
    );
    // TD-2769: extracted so the customize-columns menu backs both the desktop
    // anchored dropdown and the mobile bottom sheet. No un-anchoring is needed on
    // this surface: the mobile sheet is rendered through the action bar's slot
    // mode, which portals to <body>, so `projects.less`'s anchored-dropdown rules
    // (scoped under `.app-body.page-projects-listing .table-responsive`) never
    // reach it (TD-2772).
    // TD-2772 — `anchored` carries the desktop dropdown's positioning and chrome.
    // The sheet-hosted copy does not get it, so there is nothing to reset: the
    // panel's interior is unscoped (`customize-columns.less`) and holds wherever the
    // panel is mounted, including inside the body-portaled action bar.
    const renderProjectCustomizeMenu = ({ anchored = false } = {}) => (
      <div
        className={`customize-columns-main-wrap${
          anchored ? ' customize-columns-main-wrap--anchored' : ''
        } noselect`}
      >
        <div className="customize-columns-header">
          <div className="customize-columns-header-title">
            {
              state.profilerColumns.filter(
                (column) => column.id !== PROFILER_PROJECT_VIEW_COLUMNS.ACTIONS && column.selected
              ).length
            }{' '}
            active columns
          </div>
        </div>
        <div className="customize-columns-separator"></div>
        <div className="customize-columns-list-main-wrap">
          <div className="customize-columns-list-wrap">
            <DndContext
              modifiers={[
                restrictToVerticalAxis,
                restrictToParentElement,
                restrictToFirstScrollableAncestor
              ]}
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleColumnReorder}
            >
              <SortableContext
                items={state.profilerColumns
                  .filter((column) => column.id !== PROFILER_PROJECT_VIEW_COLUMNS.ACTIONS)
                  .map((column) => ({ id: column.id }))}
                strategy={verticalListSortingStrategy}
              >
                {state.profilerColumns
                  .filter((column) => column.id !== PROFILER_PROJECT_VIEW_COLUMNS.ACTIONS)
                  .map((item) => (
                    <SortableColumnItem
                      key={item.id}
                      item={item}
                      onClick={() => handleToggleColumnSelected(item)}
                    />
                  ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
        <div className="customize-columns-separator"></div>
        <div className="customize-columns-footer">
          <IconButton innerText="Reset" onClick={handleResetColumns} />
          <IconButton
            type={ICON_BUTTON_TYPES.BLUE}
            wrapClassName="customize-columns-financials-only-button"
            icon={
              isFinancialsOnlyActive()
                ? ICON_BUTTON_ICONS.TOGGLE_ON2
                : ICON_BUTTON_ICONS.TOGGLE_OFF2
            }
            innerText="Financials"
            onClick={handleToggleFinancialsOnly}
            filled={isFinancialsOnlyActive()}
          />
        </div>
      </div>
    );

    const getSortTooltipText = () => {
      if (
        state.isProfilerViewMenuToggled ||
        state.isSortToggled ||
        state.isProfilerCustomizeColumnsToggled
      )
        return '';
      if (!state.currentResults.length) return 'Sort is unavailable at this moment';
      if (state.effectiveSort === 'default') return 'Sort';
      return state.profilerSortOptions.find((o) => o.value === state.effectiveSort)?.name || 'Sort';
    };

    // TD-2769 — the mobile bar's sheets, rendered as siblings of the bar rather than
    // inside it. `MobileSheet` is a native <dialog>: `showModal()` promotes it to the
    // top layer without moving it in the DOM, so it keeps its ancestors and its
    // styling. Handing it to the bar as a slot child would instead drag it through
    // the bar's portal to <body>, which is what stranded the page-scoped
    // Customize-columns rules before TD-2772.
    const renderProjectMobileSheets = () => (
      <>
        {isProfilerListVisible(state.profilerView) && (
          <MobileSheet
            isOpen={state.isProfilerCustomizeColumnsToggled}
            onClose={state.toggleProfilerCustomizeColumns}
            title="Customize columns"
          >
            {renderProjectCustomizeMenu()}
          </MobileSheet>
        )}
        <MobileSheet
          isOpen={state.isProfilerViewMenuToggled}
          onClose={state.toggleProfilerViewMenu}
          title="View"
        >
          {renderProjectViewMenu('mobile-sheet-options')}
        </MobileSheet>
        <MobileSheet isOpen={state.isSortToggled} onClose={state.toggleSort} title="Sort by">
          {renderProjectSortMenu('mobile-sheet-options')}
        </MobileSheet>
      </>
    );

    const renderProjectToolbar = ({
      harmonized = false,
      feedbackSlot: feedback = null,
      trailingActionsSlot: trailing = null
    } = {}) => (
      <>
        {isProfilerListVisible(state.profilerView) && (
          <>
            {isUnifiedFiltersEnabled && (
              <AddFilterAnchor
                store={state}
                variant={state.layout === 'mobile' ? 'mobile' : 'desktop'}
                harmonized={harmonized}
                iconOnly={!harmonized}
              />
            )}
            {wrapAnchor(
              harmonized,
              <>
                <IconButton
                  type={ICON_BUTTON_TYPES.BLUE}
                  icon={ICON_BUTTON_ICONS.TABLE}
                  {...(harmonized
                    ? { innerText: 'Customize columns', wrapClassName: 'pv-toolbar-text-button' }
                    : {
                        tooltipText:
                          state.isProfilerViewMenuToggled ||
                          state.isSortToggled ||
                          state.isProfilerCustomizeColumnsToggled
                            ? ''
                            : 'Customize columns'
                      })}
                  id="customize-columns-button"
                  onClick={state.toggleProfilerCustomizeColumns}
                  filled={state.isProfilerCustomizeColumnsToggled}
                />
                {state.layout === 'desktop' &&
                  state.isProfilerCustomizeColumnsToggled &&
                  renderProjectCustomizeMenu({ anchored: true })}
              </>
            )}
          </>
        )}
        {harmonized && feedback}
        {wrapAnchor(
          harmonized,
          <>
            <IconButton
              id="list-change-btn"
              icon={PROFILER_VIEWS_LIST.find((view) => view.value === state.profilerView)?.icon}
              type={ICON_BUTTON_TYPES.BLUE}
              tooltipText={
                state.isProfilerViewMenuToggled ||
                state.isSortToggled ||
                state.isProfilerCustomizeColumnsToggled
                  ? ''
                  : 'Switch view'
              }
              onClick={state.toggleProfilerViewMenu}
              filled={state.isProfilerViewMenuToggled}
            />
            {state.layout === 'desktop' &&
              state.isProfilerViewMenuToggled &&
              renderProjectViewMenu()}
          </>
        )}
        {wrapAnchor(
          harmonized,
          <>
            <IconButton
              id="sort-btn"
              icon={
                state.effectiveSort !== 'default'
                  ? ICON_BUTTON_ICONS.OCTI_LIST_ORDERED
                  : ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED
              }
              type={ICON_BUTTON_TYPES.BLUE}
              tooltipText={getSortTooltipText()}
              tooltipPosition={
                state.layout === 'mobile' && state.effectiveSort !== 'default'
                  ? 'bottom-end'
                  : 'bottom-center'
              }
              onClick={state.toggleSort}
              filled={state.isSortToggled || state.effectiveSort !== 'default'}
            />
            {state.layout === 'desktop' && state.isSortToggled && renderProjectSortMenu()}
          </>
        )}
        {harmonized && trailing}
      </>
    );

    return (
      <>
        <div
          className="table-responsive"
          style={
            state.isSortToggled || state.isProfilerCustomizeColumnsToggled
              ? { minHeight: state.layout === 'desktop' ? '600px' : '660px' }
              : {}
          }
        >
          {state.currentResults.length === 0 ? (
            <div className="no-data">No items for this tab</div>
          ) : (
            <>
              {/* V2.0 — Spotlights bar in its own row above the column headers,
                visible only on the List view (same gate as SearchResults). On
                Industry-derived projects, hide High matches per V1.4. The
                `at-project-view` modifier picks up the project-specific
                spacing override (more breathing room above the bar, tighter
                gap to the column-headers row). */}
              {/* Unified-filter model moves the Add-filter trigger into the
                right-aligned actions cluster (with Customize-columns), so this
                standalone row only carries the legacy SpotlightsBar (flag-off). */}
              {!isUnifiedFiltersEnabled && isProfilerListVisible(state.profilerView) && (
                <div className="results-header-top-row at-project-view">
                  <SpotlightsBar
                    activeSpotlights={state.activeSpotlights}
                    spotlightCounts={state.spotlightCounts}
                    onToggleSpotlight={state.toggleSpotlight}
                    excludedSpotlightIds={state.categoryExcludedSpotlightIds}
                  />
                </div>
              )}
              {/* TD-2575 — harmonized results toolbar (DESKTOP only): one right-aligned
                row matching the Search-results bar, shown across list/heatmap/chart.
                Add-filter / Customize are list-only (inside the render-fn);
                Request-feedback + info + download arrive from the parent
                (`ProfilerProjectView`) as slots. On mobile the toolbar stays in the grid
                header and the page-header actions stay in the fixed bottom bar; a
                follow-up unifies mobile into an enrichment-style bottom action bar for
                Search + Projects. */}
              {/* TD-2575 → the harmonized bar now sits ABOVE the project tab strip
                (per request): it's portaled into the parent's mount node so it
                renders above the three tabs while staying driven by this store
                (reactivity holds — portals keep the React tree, only the DOM
                position moves). The chip rail below stays where it is. Portals
                only when the parent mount exists (flag-on desktop). */}
              {isUnifiedFiltersEnabled &&
                state.layout === 'desktop' &&
                toolbarMountNode &&
                createPortal(
                  <div className="project-sort-wrapper project-search-filters-wrapper">
                    {renderProjectToolbar({
                      harmonized: true,
                      feedbackSlot,
                      trailingActionsSlot
                    })}
                  </div>,
                  toolbarMountNode
                )}
              {/* Active filters surface as labelled, removable chips between the
                top row and the column headers. A single rail serves both
                breakpoints here (the project view keeps this region visible on
                mobile; see isPillRailVisible). */}
              {isUnifiedFiltersEnabled ? (
                <UnifiedChipRail
                  store={state}
                  variant="desktop"
                  visible={isPillRailVisible}
                  // TD-2601 — mass exclusion only makes sense on the Results tab.
                  // On the Excluded tab the rows are already excluded, so hide the
                  // control (its "exclude the rest" path would target the Results
                  // set). Filtering the excluded list stays available.
                  showBulkExclude={forTab === SELECTED_PROJECT_TABS.RESULTS}
                />
              ) : (
                <PillFilterRail store={state} variant="desktop" visible={isPillRailVisible} />
              )}
              {/* TD-2733 — with the column labels and the metric controls gone, this strip
                holds only the in-grid sort/customize wrapper, which renders on mobile or
                with the unified-filters flag off. On desktop with the flag on (all four
                environments) it would be an empty sticky bar with a border, so it is not
                rendered at all. Unmounting rather than hiding is deliberate: the hook
                feeding `--project-view-header-height` is driven by a content-box
                ResizeObserver plus a mutation observer, so a mount/unmount refreshes the
                var while a `display: none` would not reliably. */}
              {hasHeaderStrip && (
                <div
                  className="header profiler-search-cols at-project-results show-also-on-mobile"
                  // TD-2733 — the column labels and the heatmap's metric controls both left
                  // this row, so it is a right-aligned toolbar in every view now and holds
                  // nothing but the sort/customize wrapper.
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end'
                  }}
                >
                  <div
                    className="col actions profiler-search-actions"
                    style={{
                      minWidth: profilerColumns.find(
                        (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.ACTIONS
                      ).minWidth
                    }}
                  >
                    {/* TD-2769: on mobile this in-grid toolbar is retired — its
                      actions moved into the bottom ResultsMobileActionBar below.
                      Only flag-off desktop still renders it here (flag-off mobile
                      uses the bottom bar too, so this avoids a duplicate). */}
                    {!isUnifiedFiltersEnabled && state.layout === 'desktop' && (
                      <div className="project-sort-wrapper">{renderProjectToolbar()}</div>
                    )}
                  </div>
                </div>
              )}

              {state.profilerView === PROFILER_VIEWS.HEATMAP && (
                <ProfilerHeatmapSection
                  controls={state}
                  // TD-2733 — the tiles read the filtered set so a chip removal redraws both
                  // panes together. `currentResults` stays unfiltered for the Chart view and
                  // the "X out of Y" denominators.
                  results={state.filteredCurrentResults}
                  searchId={project.searchId}
                  searchCategory={project.searchCategory}
                  groupProperty={state.profilerHeatmapGroup}
                  layout={state.layout}
                  isCollapsed={state.isProfilerHeatmapCollapsed}
                  onToggleCollapse={state.toggleProfilerHeatmapCollapsed}
                  isLoading={state.isLoading}
                  hasResults={state.filteredCurrentResults.length > 0}
                  // Results and Excluded are the same component with a different set, so
                  // the treemap has to be rebuilt rather than re-laid-out on tab switch.
                  heatmapKey={forTab}
                  showCagr4Y={
                    state.profilerHeatmapColorCoding ===
                      UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['CAGR 4Y (%)'] ||
                    (!!profilerColumns.find(
                      (column) =>
                        column.id === PROFILER_PROJECT_VIEW_COLUMNS.CAGR4Y && column.selected
                    ) &&
                      state.profilerHeatmapColorCoding !==
                        UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['CAGR 3Y (%)'])
                  }
                  showEbitMargin={!profilerAccessStore.isProfilerLiteOnly}
                  displayDetails={displayDetails}
                  getIsExcludingResult={getIsExcludingResult}
                  excludeResult={excludeResult}
                  setToggleProfilerHeatmapGroupingCallback={
                    state.setToggleProfilerHeatmapGroupingCallback
                  }
                />
              )}

              {/* TD-2733 — the column-header row sits directly above the rows instead of
                inside the sticky `.header.at-project-results`, so the heatmap can stack
                above it. It carries its own `position: sticky` (see
                `.cols.profiler-search-cols.detached` in projects.less) and shares its
                column-visibility predicate with the row component, so the header's grid
                template and the rows' can no longer disagree. */}
              {isProfilerListVisible(state.profilerView) && state.layout === 'desktop' && (
                <ProfilerColumnHeaderRow
                  profilerColumns={profilerColumns}
                  columnHeaderJSX={profilerColumnHeaderJSX}
                  isColumnVisible={isProjectColumnVisible}
                  gridTemplateColumns={buildProfilerProjectGridTemplate(profilerColumns, {
                    windowWidth: utilsStore.windowWidth,
                    isProfilerExtendedFinancialsToggled: state.isProfilerExtendedFinancialsToggled
                  })}
                />
              )}

              {isProfilerListVisible(state.profilerView) && (
                <>
                  {/* V2.0 — the List body maps `filteredCurrentResults`; when
                    Spotlights / column filters narrow it to nothing (the
                    unfiltered `currentResults` is non-empty — we're past the
                    "No items for this tab" guard above), surface an explicit
                    empty state instead of a blank results area. */}
                  {state.filteredCurrentResults.length === 0 && (
                    <div className="no-filter-results">No companies match the active filters.</div>
                  )}
                  {state.filteredCurrentResults
                    .slice(
                      page * selectedProjectTabPageSize,
                      (page + 1) * selectedProjectTabPageSize
                    )
                    .map((entry, index) => (
                      <ProfilerSearchResultsTabEntry
                        key={entry.id}
                        entry={entry}
                        project={project}
                        forTab={forTab}
                        excludeResult={excludeResult}
                        isExcludingResult={entriesInAction[entry.id]}
                        displayDetails={displayDetails}
                        index={index}
                        profilerColumns={profilerColumns}
                        isProfilerExtendedFinancialsToggled={
                          state.isProfilerExtendedFinancialsToggled
                        }
                        // TD-2465 — clickable Industry / Value chain / Products pills
                        onPillClick={state.togglePillFilter}
                        columnFilters={state.columnFilters}
                      />
                    ))}

                  {profilerAccessStore.isProfilerDemo &&
                    fullResultsCount > selectedProjectTabPageSize && (
                      <div className="profiler-demo-limited-results-row">
                        <div className="icon-wrap">
                          <img src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE} />
                        </div>
                        <div className="content">
                          <div className="title">Limited results shown in demo mode</div>
                          <div className="text">
                            You&apos;re viewing {selectedProjectTabPageSize} of {fullResultsCount}{' '}
                            results. Contact{' '}
                            <a href="mailto:support@transact.digital">support@transact.digital</a>{' '}
                            for a subscription to see the full list.
                          </div>
                        </div>
                      </div>
                    )}

                  <Pagination
                    show={showPagination}
                    onPageChange={(selected) => {
                      navigate(
                        (isFromAgentView ? paths.PROJECTS : paths.PROFILER_PROJECTS) +
                          '?params=' +
                          encodeProjectsParams({ ...params, selectedProjectTabPage: selected + 1 })
                      );
                      state.scrollToProjectViewScrollStartingPoint(true);
                    }}
                    page={page}
                    pages={pages}
                  />
                </>
              )}

              {state.profilerView === PROFILER_VIEWS.CHART && (
                <ProfilerBarChart
                  companies={state.currentResults}
                  onCompanyClick={displayDetails}
                  chartId={project.searchId}
                  project={project}
                  sort={state.selectedSort}
                  getIsExcludingResult={getIsExcludingResult}
                  excludeResult={excludeResult}
                />
              )}
            </>
          )}
        </div>

        {/* TD-2769: mobile results bar, in descriptor mode — Add filter / View /
            Sort inline, Download as the primary CTA, and Customize columns plus the
            parent's feedback / data-source actions demoted into the More sheet, per
            the Q1 sign-off. The bar renders these as its own real <button>s, so no
            page-scoped node travels through its portal and every control is
            keyboard- and screen-reader-reachable (the slot-mode bar carried none).
            JS-gated on the mobile layout: on desktop the results actions live in the
            portal toolbar (the single source on wide screens, and its spacer never
            shows), so mounting renderProjectToolbar() here too would duplicate their
            ids and mount a second AddFilterPanel. Non-results tabs, where this child
            isn't mounted, get the project-level controls from the parent's own bar
            instead.
            TD-2770 — rendered as a SIBLING OUTSIDE .table-responsive: the bar's
            in-flow spacer must not live in the heatmap's scroll container, or
            ProfilerHeatmap's ResizeObserver drives an endless resize -> relayout
            loop that freezes the renderer on the mobile heatmap view. */}
        {state.layout === 'mobile' && (
          <>
            <ResultsMobileActionBar
              actions={[
                ...(isUnifiedFiltersEnabled && isProfilerListVisible(state.profilerView)
                  ? [
                      {
                        key: 'add-filter',
                        label: 'Add filter',
                        icon: ICON_BUTTON_ICONS.FUNNEL,
                        // Deliberately write-only: reading `isAddFilterPanelOpen` here
                        // would put the flag inside this (very large) observer and
                        // bring back the TD-2626 open lag that AddFilterAnchor was
                        // extracted to fix. The panel owns its own open state.
                        onClick: () => state.openAddFilterPanel()
                      }
                    ]
                  : []),
                {
                  key: 'view',
                  label: 'View',
                  icon: PROFILER_VIEWS_LIST.find((view) => view.value === state.profilerView)
                    ?.icon,
                  onClick: state.toggleProfilerViewMenu,
                  active: state.isProfilerViewMenuToggled
                },
                {
                  key: 'sort',
                  label: 'Sort',
                  icon:
                    state.effectiveSort !== 'default'
                      ? ICON_BUTTON_ICONS.OCTI_LIST_ORDERED
                      : ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED,
                  onClick: state.toggleSort,
                  active: state.isSortToggled || state.effectiveSort !== 'default'
                }
              ]}
              primary={mobilePrimaryAction}
              moreActions={[
                ...(isProfilerListVisible(state.profilerView)
                  ? [
                      {
                        key: 'customize-columns',
                        label: 'Customize columns',
                        icon: ICON_BUTTON_ICONS.TABLE,
                        onClick: state.toggleProfilerCustomizeColumns
                      }
                    ]
                  : []),
                ...mobileMoreActions
              ]}
              ariaLabel="Project results actions"
              breakpoint="to-1200"
            />
            {renderProjectMobileSheets()}
            {/* The panel is its own observer, so mounting it here keeps the bar's
                Add-filter button write-only. `variant` matches the layout gate, so
                there is never a desktop + mobile double mount. */}
            {isUnifiedFiltersEnabled && <AddFilterPanel store={state} variant="mobile" />}
          </>
        )}
      </>
    );
  }
);

export default ProfilerSearchResultsTabView;
